import { CSSProperties, ChangeEvent } from "react";

type FieldType = "text" | "number" | "date" | "time" | "email" | "tel" | "select" | "textarea";

type Props = {
  label: string;
  value: string | number | null | undefined;
  editing: boolean;
  type?: FieldType;
  options?: string[];
  onChange?: (next: string) => void;
  display?: string;
  style?: CSSProperties;
};

const LABEL_STYLE: CSSProperties = {
  fontSize: 10,
  textTransform: "uppercase",
  letterSpacing: ".08em",
  color: "var(--fg-subtle)",
  fontWeight: 600,
};

const VALUE_STYLE: CSSProperties = {
  fontSize: 14,
  marginTop: 4,
};

const INPUT_STYLE: CSSProperties = {
  width: "100%",
  marginTop: 4,
  padding: "5px 8px",
  fontSize: 14,
  fontFamily: "var(--font-body)",
  color: "var(--fg)",
  background: "var(--bg-card)",
  border: "1px dashed var(--brand-700)",
  borderRadius: 6,
  outline: "none",
  boxSizing: "border-box",
};

export function EField({
  label,
  value,
  editing,
  type = "text",
  options,
  onChange,
  display,
  style,
}: Props) {
  const stringValue = value == null ? "" : String(value);
  const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    onChange?.(e.target.value);
  };

  if (!editing) {
    return (
      <div style={style}>
        <div style={LABEL_STYLE}>{label}</div>
        <div style={VALUE_STYLE}>{display ?? (stringValue || "—")}</div>
      </div>
    );
  }

  if (type === "select" && options) {
    return (
      <div style={style}>
        <div style={LABEL_STYLE}>{label}</div>
        <select
          value={stringValue}
          onChange={handleChange}
          style={INPUT_STYLE}
        >
          {options.map(opt => (
            <option key={opt} value={opt}>{opt}</option>
          ))}
        </select>
      </div>
    );
  }

  if (type === "textarea") {
    return (
      <div style={style}>
        <div style={LABEL_STYLE}>{label}</div>
        <textarea
          value={stringValue}
          onChange={handleChange}
          style={{ ...INPUT_STYLE, minHeight: 70, resize: "vertical" }}
        />
      </div>
    );
  }

  return (
    <div style={style}>
      <div style={LABEL_STYLE}>{label}</div>
      <input
        type={type}
        value={stringValue}
        onChange={handleChange}
        style={INPUT_STYLE}
      />
    </div>
  );
}
